import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import { useTimer } from '../contexts/TimerContext';
import { useSettings } from '../contexts/SettingsContext';
import { api } from '../config/api';
import ProgressStepper from '../components/ProgressStepper';
import CancelTransactionPopup from '../components/CancelTransactionPopup';
import RazorpayPayment from '../components/RazorpayPayment';
import EasebuzzPayment from '../components/EasebuzzPayment';
import TermsConditionsPopup from '../components/TermsConditionsPopup';
import PhoneVerificationPopup from '../components/PhoneVerificationPopup';
import RegistrationPopup from '../components/RegistrationPopup';
import OTPEntryPopup from '../components/OTPEntryPopup';
import GuestCheckoutPopup from '../components/GuestCheckoutPopup';
import BookingConfirmationPopup from '../components/BookingConfirmationPopup';
import './PaymentPage.css';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
   const { getPaymentSettings, instantRefresh } = useSettings();
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [guestUserDetails, setGuestUserDetails] = useState(null);
  const { movie: globalMovie, showtime: globalShowtime, theater: globalTheater, selectedSeats: globalSelectedSeats, selectedSnacks, userDetails: globalUserDetails, setUserDetails, clearBooking, setMovie, setShowtime, setTheater, setSelectedSeats, vistaTransactionId: bookingTransactionId, setVistaTransactionId, setQuantity } = useBooking();
  const { isExpired, resetTimer } = useTimer();
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [showGuestCheckoutPopup, setShowGuestCheckoutPopup] = useState(false);
  const [showOTPPopup, setShowOTPPopup] = useState(false);
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [registrationPhone, setRegistrationPhone] = useState('');
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [bookingConfirmationDetails, setBookingConfirmationDetails] = useState(null);
  const [isProcessingBooking, setIsProcessingBooking] = useState(false);
  // ── Google-login mobile capture (BMS-style: no OTP) ──
  const [showGoogleMobileModal, setShowGoogleMobileModal] = useState(false);
  const [googleMobile, setGoogleMobile] = useState('');
  const [googleMobileSubmitting, setGoogleMobileSubmitting] = useState(false);
  const [googleMobileError, setGoogleMobileError] = useState('');
  const [convenienceFeeSettings, setConvenienceFeeSettings] = useState({
    enabled: false,
    type: 'fixed',
    value: 0,
  });
  const [bookingFeeTiers, setBookingFeeTiers] = useState(null);
  const [paymentToken, setPaymentToken] = useState(null);
  const [loadingBookingFromToken, setLoadingBookingFromToken] = useState(false);
  const [dbCinemaData, setDbCinemaData] = useState(null); // State to store fetched cinema data
  const [bookingFromToken, setBookingFromToken] = useState(null); // Store full booking data from token
  const [movieFromDb, setMovieFromDb] = useState(null); // Store movie fetched from database
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [launchOffer, setLaunchOffer] = useState(null);
  const { settings } = useSettings();
  const fbGstPercent = settings?.fnbGst ?? 0;
  // const fbGstPercent = settings?.fnbGst || 0;

  // const roundTo2 = (value) => Number((value + 0.00001).toFixed(2))
  // roundTo2 helper for safe currency math
  const roundTo2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

  // Whether to show the launch-offer free 35g popcorn line item.
  // For fresh booking flow: based on `launchOffer.active` (fetched on mount).
  // For payment-token flow: based on `bookingFromToken.promotionalItems[]`.
  const showLaunchOfferLine = () => {
    if (paymentToken && bookingFromToken) {
      const items =
        bookingFromToken.bookingDetails?.promotionalItems ||
        bookingFromToken.promotionalItems ||
        [];
      return Array.isArray(items) && items.length > 0;
    }
    return !!(launchOffer && launchOffer.active);
  };
  const launchOfferLabel = () => {
    if (paymentToken && bookingFromToken) {
      const items =
        bookingFromToken.bookingDetails?.promotionalItems ||
        bookingFromToken.promotionalItems ||
        [];
      if (Array.isArray(items) && items[0]?.name) return items[0].name;
    }
    return (launchOffer && launchOffer.itemName) || "Complimentary 35g Popcorn";
  };


  const getSnacksTotalWithGST = () => {
    const base = getSnacksPrice();
    const total = base * (1 + fbGstPercent / 100);
    return roundTo2(total);
    // const gst = (base * fbGstPercent) / 100;
    // return Number((base + 0.00001).toFixed(2));
  };

 


  // Load payment settings from admin API
  const loadPaymentSettings = useCallback(async () => {
    try {
      const settings = await getPaymentSettings();
      setPaymentSettings(settings);
      // console.log('Payment settings loaded:', settings);
      
      // Update convenience fee settings from payment settings
      setConvenienceFeeSettings({
        enabled: settings.enableConvenienceFee || false,
        type: settings.convenienceFeeType || 'fixed',
        value: settings.convenienceFeeValue || 0,
      });
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  }, [getPaymentSettings]);

  useEffect(() => {
    loadPaymentSettings();
  }, [loadPaymentSettings]);

  // Fetch launch-offer status — if active, show free 35g popcorn line in summary.
  useEffect(() => {
    let cancelled = false;
    api.getLaunchOfferStatus()
      .then((status) => {
        if (!cancelled) setLaunchOffer(status);
      })
      .catch(() => {
        if (!cancelled) setLaunchOffer({ active: false });
      });
    return () => { cancelled = true; };
  }, []);

  // Listen for admin settings updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_settings_updated') {
        // console.log('Admin settings updated, refreshing payment settings...');
        loadPaymentSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Also check for admin updates on page focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const lastAdminUpdate = localStorage.getItem('admin_settings_updated');
        const lastUpdate = localStorage.getItem('last_payment_settings_update');
        
        if (lastAdminUpdate && (!lastUpdate || parseInt(lastAdminUpdate) > parseInt(lastUpdate))) {
          // console.log('Page focused, admin settings were updated, refreshing payment settings...');
          loadPaymentSettings();
          localStorage.setItem('last_payment_settings_update', Date.now().toString());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);




  // Pending booking state
  const [pendingBookingId, setPendingBookingId] = useState(null);
  const [isCreatingPendingBooking, setIsCreatingPendingBooking] = useState(false);
  // Shared terms and conditions acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Terms & Conditions popup state
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [termsContentType, setTermsContentType] = useState('terms-and-conditions');
  // Back navigation confirmation
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancellingTransaction, setIsCancellingTransaction] = useState(false);
  const isNavigatingBackRef = useRef(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  // Handle Terms & Conditions popup
  const handleTermsLinkClick = (contentType) => {
    setTermsContentType(contentType);
    setShowTermsPopup(true);
  };

  const handleCloseTermsPopup = () => {
    setShowTermsPopup(false);
  };

  const handleTermsAccepted = (contentType) => {
    // Optional: Auto-check the main terms checkbox if user accepts from popup
    if (contentType === 'terms-and-conditions') {
      setTermsAccepted(true);
    }
  };
  

  // Helper function to get the poster image (poster > banner > image > default)
  const getMovieImage = (movieObj) => {
    if (!movieObj) return "/assets/img/film-banner-booking.png";
    return movieObj.poster || movieObj.posterUrl || movieObj.banner || movieObj.bannerUrl || movieObj.image || "/assets/img/film-banner-booking.png";
  };

  // Fetch movie details from database if movieId is available
  // Skip if movieId is a Vista movie ID (contains "HO") - we'll use bookingFromToken data instead
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const fetchMovieFromDb = async () => {
      // Get movieId from various sources
      const movieId = 
        bookingFromToken?.movieId || 
        bookingFromToken?.movie?.id || 
        location.state?.movie?.id || 
        globalMovie?.id;
      
      // Skip database fetch if:
      // 1. We have a payment token and the movie ID looks like a Vista movie ID (contains "HO")
      // 2. We already have movieFromDb
      // 3. No movieId available
      if (!movieId || movieFromDb || !isMounted) {
        return;
      }
      
      // Check if movieId is a Vista movie ID (contains "HO" pattern like "1003HO00000355")
      // Database UUIDs look like "a5522cca-4eca-40b5-82cf-36db620b6607" (with hyphens)
      const isVistaMovieId = typeof movieId === 'string' && movieId.includes('HO');
      
      // If it's a Vista movie ID and we have payment token, skip database fetch
      // We'll use the movie data from bookingFromToken instead
      if (isVistaMovieId && paymentToken) {
        // console.log('Skipping database fetch for Vista movie ID:', movieId, '- using bookingFromToken data');
        return;
      }
      
      // Only fetch from database if it looks like a database UUID (not a Vista movie ID)
      // Database UUIDs typically have hyphens and are 36 characters long
      const isDatabaseId = typeof movieId === 'string' && 
        (movieId.includes('-') || movieId.length === 36 || !movieId.includes('HO'));
      
      if (!isDatabaseId) {
        // console.log('Skipping database fetch - movieId does not look like a database UUID:', movieId);
        return;
      }
      
      try {
        // console.log('Fetching movie details from database for movieId:', movieId);
        const movieData = await api.getMovieById(movieId);
        if (isMounted && movieData && movieData.success && movieData.data) {
          // console.log('Movie fetched from database:', movieData.data);
          setMovieFromDb(movieData.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching movie from database:', error);
          // Don't show error to user - we'll fall back to bookingFromToken data
        }
      }
    };

    fetchMovieFromDb();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [bookingFromToken?.movieId, bookingFromToken?.movie?.id, location.state?.movie?.id, globalMovie?.id, movieFromDb, paymentToken]);

  // Get booking data from navigation state or global state
  // Prioritize movieFromDb (from database) > bookingFromToken > location state > global state
  const movie = useMemo(() => {
    // First priority: Movie fetched from database (has correct poster URL)
    if (movieFromDb) {
      return movieFromDb;
    }
    
    // Second priority: For payment link bookings, use movie from bookingFromToken
    if (paymentToken && bookingFromToken?.movie) {
      return bookingFromToken.movie;
    }
    
    // Third priority: Location state or global state
    const stateMovie = location.state?.movie || globalMovie;
    if (stateMovie) {
      return stateMovie;
    }
    
    // Fallback to default
    return {
      title: "THE PET DETECTIVE",
      genre: "Adventure",
      language: "Malayalam",
      poster: "/assets/img/film-banner-booking.png"
    };
  }, [movieFromDb, location.state?.movie, globalMovie, paymentToken, bookingFromToken]);
  
  const showtime = useMemo(() => {
    // For payment link bookings, use showtime from bookingFromToken
    if (paymentToken && bookingFromToken?.showtime) {
      const showtimeData = bookingFromToken.showtime;
      const showDate = showtimeData.showDate || bookingFromToken.showDate;
      const showTime = showtimeData.showTime || bookingFromToken.showTime;
      
      if (showDate && showTime) {
        // Format showtime for display
        const showDateObj = new Date(showDate);
        const formattedDate = showDateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        
        // Format time (convert 24-hour to 12-hour if needed)
        let formattedTime = showTime;
        if (showTime.includes(':')) {
          const [hours, minutes] = showTime.split(':');
          const hour24 = parseInt(hours, 10);
          const hour12 = hour24 % 12 || 12;
          const ampm = hour24 >= 12 ? 'PM' : 'AM';
          formattedTime = `${hour12}:${minutes} ${ampm}`;
        }
        
        return {
          date: formattedDate,
          time: formattedTime,
          originalDate: showDate,
          originalTime: showTime,
          screenName: showtimeData.screenName || bookingFromToken.screenName || null,
          screenId: showtimeData.screenName || bookingFromToken.screenName || 'screen-1',
          id: showtimeData.id || showtimeData.vistaShowId || null,
          vistaShowId: showtimeData.id || showtimeData.vistaShowId || null,
        };
      }
    }
    
    // Fallback to location state or global state
    return location.state?.showtime || globalShowtime || {
      date: "Wed, 22 Oct",
      time: "7:50 PM"
    };
  }, [location.state?.showtime, globalShowtime, paymentToken, bookingFromToken]);
  
  const theater = useMemo(() => {
    // For payment link bookings, use theater from bookingFromToken
    if (paymentToken && bookingFromToken) {
      if (bookingFromToken.theater) {
        return bookingFromToken.theater;
      }
      if (bookingFromToken.cinema) {
        return bookingFromToken.cinema;
      }
    }
    
    // Fallback to location state or global state
    return location.state?.theater || globalTheater || {
      name: "My Cinemas",
      location: "KSRTC Complex, Angamaly, Kerala"
    };
  }, [location.state?.theater, globalTheater, paymentToken, bookingFromToken]);

  const selectedSeats = useMemo(() => {
    // For payment link bookings, use seats from bookingFromToken
    if (paymentToken && bookingFromToken) {
      if (bookingFromToken.selectedSeats && bookingFromToken.selectedSeats.length > 0) {
        return bookingFromToken.selectedSeats;
      }
      if (bookingFromToken.seats && bookingFromToken.seats.length > 0) {
        return bookingFromToken.seats;
      }
    }
    
    // Fallback to location state or global state
    return location.state?.selectedSeats || globalSelectedSeats || [];
  }, [location.state?.selectedSeats, globalSelectedSeats, paymentToken, bookingFromToken]);
  const snacks = location.state?.snacks || selectedSnacks || [];
  const userDetails = guestUserDetails || globalUserDetails || (bookingFromToken ? {
    name: bookingFromToken.customerName || bookingFromToken.userDetails?.name || '',
    email: bookingFromToken.customerEmail || bookingFromToken.userDetails?.email || '',
    phone: bookingFromToken.customerPhone || bookingFromToken.userDetails?.phone || '',
    mobile: bookingFromToken.customerPhone || bookingFromToken.userDetails?.phone || '',
    verified: true,
  } : null);

  // Fetch full cinema details from database when theater changes
  useEffect(() => {
    const fetchCinemaDetails = async () => {
      if (!theater) {
        setDbCinemaData(null);
        return;
      }

      let databaseCinemaId = theater?.id;
      
      // Check if databaseCinemaId is a UUID format (contains hyphens) or a Vista cinema ID
      // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
      const isUUID = databaseCinemaId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(databaseCinemaId);
      
      // Try to fetch by database ID first (only if it's a UUID)
      if (databaseCinemaId && isUUID) {
        try {
          const cinemaData = await api.getCinemaById(databaseCinemaId);
          if (cinemaData) {
            setDbCinemaData(cinemaData);
            return;
          }
        } catch (error) {
          console.warn('Failed to fetch cinema by database ID:', error);
        }
      }
      
      // If not a UUID or fetch failed, try to find by Vista cinema ID
      const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid || (!isUUID ? databaseCinemaId : null);
      if (vistaCinemaId) {
        try {
          // Fetch all cinemas and find by Vista ID
          const allCinemas = await api.getCinemas({ limit: 1000 });
          const cinemaByVistaId = allCinemas.find(c => 
            c.vistaCinemaId === vistaCinemaId || 
            c.vistacinemaid === vistaCinemaId || 
            c.vista_cinema_id === vistaCinemaId ||
            String(c.vistaCinemaId) === String(vistaCinemaId) ||
            String(c.vistacinemaid) === String(vistaCinemaId) ||
            String(c.vista_cinema_id) === String(vistaCinemaId)
          );
          if (cinemaByVistaId && cinemaByVistaId.id) {
            // Found cinema by Vista ID, now fetch full details using database UUID
            const cinemaData = await api.getCinemaById(cinemaByVistaId.id);
            if (cinemaData) {
              setDbCinemaData(cinemaData);
              return;
            }
          }
        } catch (vistaError) {
          console.warn('Failed to find cinema by Vista ID:', vistaError);
        }
      }
      
      // If all fetches failed, set to null to use theater data as fallback
      setDbCinemaData(null);
    };

    fetchCinemaDetails();
  }, [theater]);

  // Check for payment token in URL FIRST - this must run before any redirect checks
  // This ensures payment link flow is completely separate from normal booking flow
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Set token immediately to prevent redirects
      if (isMounted) {
        setPaymentToken(token);
        setLoadingBookingFromToken(true);
      }
      
      // Fetch booking data by payment token
      api.getBookingByPaymentToken(token)
        .then((bookingData) => {
          // Only update state if component is still mounted
          if (!isMounted) return;
          
          // Store the full booking data for payment link flow
          setBookingFromToken(bookingData);

          // console.log("BOOKING TOKEN DATA:", bookingData);
          
          // Prefill booking context with fetched data
          if (bookingData.movie) {
            setMovie(bookingData.movie);
          }
          // Backend returns 'theater' not 'cinema'
          if (bookingData.theater || bookingData.cinema) {
            setTheater(bookingData.theater || bookingData.cinema);
          }
          // Backend returns showtime nested in showtime object
          const showtimeData = bookingData.showtime || {};
          const showDate = showtimeData.showDate || bookingData.showDate;
          const showTime = showtimeData.showTime || bookingData.showTime;
          
          if (showDate && showTime) {
            // Format showtime for display
            const showDateObj = new Date(showDate);
            const formattedDate = showDateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            
            // Format time (convert 24-hour to 12-hour if needed)
            let formattedTime = showTime;
            if (showTime.includes(':')) {
              const [hours, minutes] = showTime.split(':');
              const hour24 = parseInt(hours, 10);
              const hour12 = hour24 % 12 || 12;
              const ampm = hour24 >= 12 ? 'PM' : 'AM';
              formattedTime = `${hour12}:${minutes} ${ampm}`;
            }
            
            setShowtime({
              date: formattedDate,
              time: formattedTime,
              originalDate: showDate,
              originalTime: showTime,
              screenName: showtimeData.screenName || bookingData.screenName || null,
              screenId: showtimeData.screenName || bookingData.screenName || 'screen-1',
            });
          }
          // Backend returns 'selectedSeats' or 'seats'
          if (bookingData.selectedSeats && bookingData.selectedSeats.length > 0) {
            setSelectedSeats(bookingData.selectedSeats);
          } else if (bookingData.seats && bookingData.seats.length > 0) {
            setSelectedSeats(bookingData.seats);
          }
          
          // Prefill user details - backend returns nested in 'userDetails' object or 'bookingDetails'
          const userDetailsData = bookingData.userDetails || {};
          const bookingDetailsData = bookingData.bookingDetails || {};
          
          // Check multiple paths for customer details
          const customerName = 
            userDetailsData.name || 
            bookingDetailsData.customerName || 
            bookingData.customerName || 
            '';
          const customerEmail = 
            userDetailsData.email || 
            bookingDetailsData.customerEmail || 
            bookingData.customerEmail || 
            '';
          const customerPhone = 
            userDetailsData.phone || 
            bookingDetailsData.customerPhone || 
            bookingData.customerPhone || 
            '';
          
          // Always set userDetails for payment token bookings, even if values are empty
          // This ensures the payment form can render
          setUserDetails({
            name: customerName,
            email: customerEmail,
            phone: customerPhone,
            mobile: customerPhone,
            verified: true, // Mark as verified since admin created the booking
            // Note: userId will be set when payment is processed, backend will handle user lookup/creation
          });
          setIsPhoneVerified(true);
          
          setLoadingBookingFromToken(false);
        })
        .catch((error) => {
          // Only update state if component is still mounted
          if (!isMounted) return;
          
          console.error('Error fetching booking by payment token:', error);
          
          // Check if the error is because the payment link was already used
          if (error.response?.status === 410 || error.response?.data?.error === 'Payment link already used') {
            const errorMessage = error.response?.data?.message || 'This payment link has already been used. The booking has been completed.';
            showError(errorMessage);
          } else {
            showError('Invalid or expired payment link. Please contact support.');
          }
          
          navigate('/');
          setLoadingBookingFromToken(false);
        });
    } else {
      // No token in URL - this is a normal booking flow
      if (isMounted) {
        setPaymentToken(null);
        setLoadingBookingFromToken(false);
      }
    }
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [location.search, setMovie, setTheater, setShowtime, setSelectedSeats, setUserDetails, navigate]);

  // Check if user has selected seats, redirect if not
  // Skip this check if confirmation popup is showing, loading booking from token, or payment token is present
  useEffect(() => {
    // Check for payment token in URL first (synchronously) - double check to prevent race conditions
    const urlParams = new URLSearchParams(location.search);
    const tokenInUrl = urlParams.get('token');
    
    // Don't redirect if confirmation popup is showing, loading booking, or payment token is present
    if (showConfirmationPopup || loadingBookingFromToken || paymentToken || tokenInUrl) {
      return;
    }

    const selectedSeats = globalSelectedSeats || location.state?.selectedSeats || [];
    
    // If no seats are selected, redirect back
    if (!selectedSeats || selectedSeats.length === 0) {
      // If no show is selected either, redirect to home
      if (!showtime || !theater) {
        showError('Please select a show and seats first!');
        navigate('/');
        return;
      }
      // If show is selected but no seats, redirect to seat selection
      showError('Please select your seats first!');
      navigate('/seat-selection');
      return;
    }
  }, [globalSelectedSeats, location.state?.selectedSeats, showtime, theater, navigate, showConfirmationPopup, loadingBookingFromToken, paymentToken, location.search]);

  // Handle timer expiration
  // Skip timer expiration for payment link bookings (they don't have a session timer)
  useEffect(() => {
    // Check for payment token in URL
    const urlParams = new URLSearchParams(location.search);
    const tokenInUrl = urlParams.get('token');
    
    // Don't expire payment link bookings
    if (paymentToken || tokenInUrl) {
      return;
    }
    
    if (isExpired) {
      // Show timeout message
      showError('Session expired! Your booking session has been cleared. Please start over.');
      
      // Reset timer for next session
      resetTimer();
      
      // Redirect back to showtime selection
      navigate(`/booking/${movie.id || 'empuraan'}`);
    }
  }, [isExpired, resetTimer, navigate, movie.id, paymentToken, location.search]);

  // Intercept back navigation
  useEffect(() => {
    // Skip back navigation interception for payment link bookings
    const urlParams = new URLSearchParams(location.search);
    const tokenInUrl = urlParams.get('token');
    if (paymentToken || tokenInUrl) {
      return;
    }

    const handlePopState = (e) => {
      // If we're already navigating back (confirmed), allow it
      if (isNavigatingBackRef.current) {
        return;
      }

      // Prevent default back navigation
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
      
      // Show confirmation popup
      setShowCancelConfirm(true);
    };

    // Push a state to enable back button interception
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [paymentToken, location.search]);

  // Handle cancel transaction confirmation (for PaymentPage, navigate to seat selection)
  const handleCancelTransaction = useCallback(async () => {
    setIsCancellingTransaction(true);
    
    try {
      // Clear selected seats and quantity
      setSelectedSeats([]);
      setQuantity(null);

      // Close confirmation popup
      setShowCancelConfirm(false);

      // Navigate to seat selection - reinitialization will happen when user selects quantity
      isNavigatingBackRef.current = true;
      navigate('/seat-selection', {
        state: {
          movie,
          showtime,
          theater,
          selectedDate: location.state?.selectedDate,
          showQuantityModal: true, // Flag to show quantity modal immediately
        }
      });
    } catch (error) {
      console.error('Error cancelling transaction:', error);
      showError(`Failed to cancel transaction: ${error.message || 'Please try again.'}`);
    } finally {
      setIsCancellingTransaction(false);
    }
  }, [movie, showtime, theater, navigate, location.state?.selectedDate, setQuantity, setSelectedSeats]);

  // Fetch convenience fee settings and booking fee tiers on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await api.getSettings();
        if (settings && settings.convenienceFee) {
          setConvenienceFeeSettings(settings.convenienceFee);
        }
        // Set booking fee tiers if available (tiered system takes precedence)
        if (settings && settings.bookingFeeTiers && Array.isArray(settings.bookingFeeTiers) && settings.bookingFeeTiers.length > 0) {
          setBookingFeeTiers(settings.bookingFeeTiers);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Use defaults if fetch fails
      }
    };
    fetchSettings();
  }, []);

  // Check if user is logged in, if not show the phone popup
  // Skip phone verification if payment token is present (booking already created by admin)
  useEffect(() => {
    // If payment token is present, skip phone verification (admin-created booking)
    if (paymentToken) {
      setShowPhonePopup(false);
      setShowGuestCheckoutPopup(false);
      setIsPhoneVerified(true);
      return;
    }

    // Check if userDetails exists and has required fields (userId or name) to determine if logged in
    // For guest checkout, userDetails might have email/phone but no userId
    const isLoggedIn = userDetails && (userDetails.userId || (userDetails.verified && userDetails.userId));
    const isGuest = userDetails && !userDetails.userId && (userDetails.email || userDetails.phone);
    
    if (!isLoggedIn && !isGuest) {
      // Show guest checkout popup by default
      setShowGuestCheckoutPopup(true);
      setShowPhonePopup(false);
    } else {
      setShowPhonePopup(false);
      setShowGuestCheckoutPopup(false);
      setIsPhoneVerified(true);
    }
  }, [userDetails, paymentToken]);

  // ── Detect Google user without mobile — prompt for mobile (no OTP) ──
  useEffect(() => {
    if (!isPhoneVerified || paymentToken) return;
    const isGoogleUser =
      userDetails &&
      (userDetails.authProvider === "google" || userDetails.authProvider === "both");
    const hasNoMobile = !userDetails?.mobile && !userDetails?.phone;
    if (isGoogleUser && hasNoMobile) {
      setShowGoogleMobileModal(true);
    }
  }, [isPhoneVerified, userDetails, paymentToken]);

  const handleGoogleMobileSubmit = async () => {
    const cleaned = googleMobile.replace(/\s+/g, "").replace(/^\+?91/, "");
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      setGoogleMobileError("Please enter a valid 10-digit mobile number");
      return;
    }
    setGoogleMobileError("");
    try {
      setGoogleMobileSubmitting(true);
      const updated = await api.updateUserProfile(userDetails.userId || userDetails.id, {
        mobile: cleaned,
      });
      // Reflect in local state
      const merged = {
        ...userDetails,
        mobile: cleaned,
        phone: cleaned,
      };
      setUserDetails(merged);
      try {
        localStorage.setItem("user", JSON.stringify(merged));
      } catch (e) {
        // ignore
      }
      setShowGoogleMobileModal(false);
      setGoogleMobile("");
    } catch (err) {
      console.error("Failed to save mobile:", err);
      setGoogleMobileError(
        err?.response?.error || err?.message || "Failed to save mobile. Please try again.",
      );
    } finally {
      setGoogleMobileSubmitting(false);
    }
  };

  // Handle phone verification - OTP sent, show OTP entry
  const handlePhoneVerified = (phoneNumber) => {
    // console.log('OTP sent to:', phoneNumber);
    // Store phone number temporarily so we can use it when OTP is verified
    const cleanPhone = phoneNumber.replace(/\s+/g, '').replace(/-/g, '');
    setOtpPhone(cleanPhone);
    setShowPhonePopup(false);
    setShowOTPPopup(true);
    // Also store phone temporarily in userDetails so it's available
    setUserDetails(prev => ({
      ...prev,
      phone: cleanPhone,
      mobile: cleanPhone,
      verified: false
    }));
  };

  // Handle OTP verified - user logged in
  const handleOTPVerified = (user) => {
    // console.log('OTP verified, user logged in:', user);
    // console.log('OTP verified - Full user object:', JSON.stringify(user, null, 2));
    // console.log('OTP verified - otpPhone state:', otpPhone);
    setShowOTPPopup(false);
    // Save full user details - ensure all fields are present
    // Priority: user.mobile > user.phone > otpPhone (from state)
    // The phone number MUST be set - it's critical for Razorpay
    const finalPhoneNumber = user.mobile || user.phone || otpPhone || '';
    // console.log('OTP verified - Final phone number to save:', finalPhoneNumber);
    
    if (!finalPhoneNumber) {
      console.error('WARNING: No phone number available to save!', { user, otpPhone });
    }
    
    const userDetailsToSave = {
      userId: user.userId || user.id,
      id: user.userId || user.id, // Also set id for compatibility
      name: user.name || '',
      email: user.email || '',
      phone: finalPhoneNumber, // CRITICAL: Always set phone from login
      mobile: finalPhoneNumber, // Also set mobile for compatibility
      verified: user.isVerified !== undefined ? user.isVerified : true,
    };
    // console.log('Saving user details with phone:', userDetailsToSave);
    setUserDetails(userDetailsToSave);
    setIsPhoneVerified(true);
  };

  const handleGoogleSignIn = (user) => {
    // user is already-decrypted user object from the Google login flow
    const userDetailsToSave = {
      userId: user.userId || user.id,
      id: user.userId || user.id,
      name: user.name || "",
      email: user.email || "",
      phone: user.mobile || user.phone || "",
      mobile: user.mobile || user.phone || "",
      authProvider: user.authProvider || "google",
      googleId: user.googleId || null,
      profilePictureUrl: user.profilePictureUrl || null,
      verified: true,
    };
    setUserDetails(userDetailsToSave);
    setIsPhoneVerified(true);
    setShowPhonePopup(false);
  };

  const handleShowRegistration = (phoneNumber) => {
    // console.log('Phone number not found, showing registration:', phoneNumber);
    setRegistrationPhone(phoneNumber);
    setShowPhonePopup(false);
    setShowRegistrationPopup(true);
  };

  const handleRegistrationComplete = (userData) => {
    // console.log('Registration completed:', userData);
    setUserDetails(userData);
    setIsPhoneVerified(true);
    setShowRegistrationPopup(false);
    // Here you would typically save the user data and proceed
  };

  // Handle switch to guest checkout
  const handleSwitchToGuest = () => {
    setShowPhonePopup(false);
    setShowGuestCheckoutPopup(true);
  };

  // Handle switch to login
  const handleSwitchToLogin = () => {
    setShowGuestCheckoutPopup(false);
    setShowPhonePopup(true);
  };

  // Handle guest details submission
  const handleGuestDetailsSubmit = (guestDetails) => {
    // console.log('Guest details submitted:', guestDetails);
    // Set userDetails with guest info (no userId)
    setGuestUserDetails({  // ← Use local state instead
      email: guestDetails.email,
      phone: guestDetails.phone,
      mobile: guestDetails.phone,
      name: guestDetails.name,
      verified: false,
    });
    setShowGuestCheckoutPopup(false);
    setIsPhoneVerified(true);
  };

  const handleClosePhonePopup = () => {
    setShowPhonePopup(false);
    // Navigate back to snacks page
    navigate('/snacks', {
      state: {
        movie,
        showtime,
        theater,
        selectedSeats,
        snacks: snacks // Pass snacks when navigating back
      }
    });
  };

  const handleCloseGuestCheckoutPopup = () => {
    setShowGuestCheckoutPopup(false);
    // Navigate back to snacks page
    navigate('/snacks', {
      state: {
        movie,
        showtime,
        theater,
        selectedSeats,
        snacks: snacks // Pass snacks when navigating back
      }
    });
  };

  // Handle close OTP popup
  const handleCloseOTPPopup = () => {
    setShowOTPPopup(false);
    setShowPhonePopup(true); // Go back to phone entry
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    // This will be handled by OTPEntryPopup component
  };

  const handleCloseRegistrationPopup = () => {
    setShowRegistrationPopup(false);
    setShowPhonePopup(true);
  };

  // Load pendingBookingId from sessionStorage on mount
  useEffect(() => {
    const storedBookingId = sessionStorage.getItem('pendingBookingId');
    if (storedBookingId && !pendingBookingId) {
      setPendingBookingId(storedBookingId);
    }
  }, [pendingBookingId]);

  // Create pending booking before opening payment gateway
  const createPendingBookingBeforePayment = async () => {
    // Check sessionStorage first (more reliable than state)
    const storedBookingId = sessionStorage.getItem('pendingBookingId');
    if (storedBookingId) {
      setPendingBookingId(storedBookingId);
      return storedBookingId;
    }
    
    // If we already have a pending booking in state, return it
    if (pendingBookingId) {
      return pendingBookingId;
    }

    // Skip for payment token bookings (already have pending booking)
   // Skip for payment token bookings (booking already created by backend)
if (paymentToken) {
  if (!bookingFromToken?.bookingId && !bookingFromToken?.id) {
    console.error("Payment token booking missing bookingId:", bookingFromToken);
    showError("Invalid booking data. Please contact support.");
    throw new Error("Missing bookingId in payment token booking");
  }

  const tokenBookingId = bookingFromToken.bookingId || bookingFromToken.id;
  // console.log("Using bookingId from payment token:", tokenBookingId);

  return tokenBookingId;
}

    setIsCreatingPendingBooking(true);
    try {
      // Parse showtime date and time (same logic as handlePaymentSuccess)
      let showDate = null;
      let showTime = null;
      
      if (showtime?.showDateTime || showtime?.startDateTime) {
        try {
          const dateTimeStr = showtime.showDateTime || showtime.startDateTime;
          const dateTime = new Date(dateTimeStr);
          if (!isNaN(dateTime.getTime())) {
            const year = dateTime.getFullYear();
            const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
            const day = dateTime.getDate().toString().padStart(2, '0');
            showDate = `${year}-${month}-${day}`;
            const hours = dateTime.getHours().toString().padStart(2, '0');
            const minutes = dateTime.getMinutes().toString().padStart(2, '0');
            showTime = `${hours}:${minutes}`;
          }
        } catch (e) {
          console.warn('Failed to parse showDateTime:', e);
        }
      }
      
      if (!showDate && showtime?.originalDate) {
        showDate = showtime.originalDate;
      }
      
      if (!showTime && showtime?.originalTime) {
        showTime = showtime.originalTime;
      }
      
      if (!showDate && showtime?.date) {
        if (showtime.date.includes(',')) {
          const dateParts = showtime.date.split(',');
          if (dateParts.length > 1) {
            const dayMonth = dateParts[1].trim().split(' ');
            const currentYear = new Date().getFullYear();
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthIndex = monthNames.findIndex(m => m === dayMonth[1]);
            if (monthIndex !== -1) {
              const date = new Date(currentYear, monthIndex, parseInt(dayMonth[0]));
              showDate = date.toISOString().split('T')[0];
            }
          }
        } else {
          showDate = showtime.date;
        }
      }
      
      if (!showTime && showtime?.time) {
        const timeStr = showtime.time.trim();
        const pmMatch = timeStr.match(/(\d+):(\d+)\s*(PM|pm)/);
        const amMatch = timeStr.match(/(\d+):(\d+)\s*(AM|am)/);
        if (pmMatch) {
          let hours = parseInt(pmMatch[1]);
          if (hours !== 12) hours += 12;
          const minutes = pmMatch[2].padStart(2, '0');
          showTime = `${hours}:${minutes}`;
        } else if (amMatch) {
          let hours = parseInt(amMatch[1]);
          if (hours === 12) hours = 0;
          const minutes = amMatch[2].padStart(2, '0');
          showTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
        } else {
          showTime = timeStr;
        }
      }
      
      if (!showDate) {
        showDate = new Date().toISOString().split('T')[0];
      }
      
      if (!showTime) {
        showTime = '00:00';
      }

      if (!selectedSeats || selectedSeats.length === 0) {
        throw new Error('No seats selected');
      }

      const userId = userDetails?.userId || userDetails?.id || null;
      const vistaMovieId = movie?.vistaMovieId || movie?.vistamovieid || null;
      const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid || null;
      
      // Get Vista transaction ID from multiple sources (in order of priority):
      // 1. Location state (most reliable for quick booking)
      // 2. Booking context (for normal flow)
      const locationTransId = location.state?.vistaTransactionId || null;
      const contextTransId = (bookingTransactionId && typeof bookingTransactionId === 'object' && !Array.isArray(bookingTransactionId) 
        ? bookingTransactionId.transactionId 
        : (bookingTransactionId || null));
      const vistaTransId = locationTransId || contextTransId;
      
      // Get Vista session ID from multiple sources
      const locationSessionId = location.state?.vistaSessionId || null;
      const showtimeSessionId = showtime?.id || showtime?.vistaShowId || showtime?.sessionId || null;
      const vistaSessionId = locationSessionId || showtimeSessionId;

      // Log Vista IDs for debugging
      // console.log('Vista IDs for pending booking creation:', {
      //   vistaCinemaId,
      //   vistaSessionId,
      //   vistaTransId,
      //   locationTransId,
      //   contextTransId,
      //   locationSessionId,
      //   showtimeSessionId,
      //   showtime: showtime,
      //   theater: theater,
      //   bookingTransactionId: bookingTransactionId,
      //   locationState: location.state,
      // });

      // Validate Vista IDs before creating pending booking
      if (!vistaCinemaId || !vistaSessionId || !vistaTransId) {
        const missingIds = [];
        if (!vistaCinemaId) missingIds.push('vistaCinemaId');
        if (!vistaSessionId) missingIds.push('vistaSessionId');
        if (!vistaTransId) missingIds.push('vistaTransId');
        
        console.error('Missing Vista IDs for pending booking:', {
          missing: missingIds,
          vistaCinemaId,
          vistaSessionId,
          vistaTransId,
          theater: theater,
          showtime: showtime,
          locationState: location.state,
        });
        
        showError(`Missing required information: ${missingIds.join(', ')}. Please go back and try again.`);
        throw new Error(`Missing Vista IDs: ${missingIds.join(', ')}`);
      }

      // Fetch cinema data from database to ensure correct name and address
      let dbCinemaName = theater?.name || 'Unknown Cinema';
      let dbCinemaAddress = (theater?.location && theater.location.trim() !== '') ? theater.location : (theater?.address && theater.address.trim() !== '') ? theater.address : null;
      let dbCinemaId = theater?.id;

      // Check if dbCinemaId is a UUID format (contains hyphens) or a Vista cinema ID
      // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
      const isUUID = dbCinemaId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dbCinemaId);

      if (dbCinemaId && isUUID) {
        try {
          const cinemaData = await api.getCinemaById(dbCinemaId);
          if (cinemaData) {
            dbCinemaName = cinemaData.name || dbCinemaName;
            dbCinemaAddress = cinemaData.address || cinemaData.location || dbCinemaAddress;
            dbCinemaId = cinemaData.id || dbCinemaId;
          }
        } catch (error) {
          console.warn('Failed to fetch cinema from database by ID:', error);
        }
      }
      
      // If not a UUID or fetch failed, try to find by vistaCinemaId and then fetch by its actual ID
      const vistaCinemaIdForSearch = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid || (!isUUID ? dbCinemaId : null);
      if (vistaCinemaIdForSearch) {
        try {
          const allCinemas = await api.getCinemas({ limit: 1000 });
          const matchingCinema = allCinemas.find(c => 
            c.vistaCinemaId === vistaCinemaIdForSearch || 
            c.vistacinemaid === vistaCinemaIdForSearch || 
            c.vista_cinema_id === vistaCinemaIdForSearch ||
            String(c.vistaCinemaId) === String(vistaCinemaIdForSearch) ||
            String(c.vistacinemaid) === String(vistaCinemaIdForSearch) ||
            String(c.vista_cinema_id) === String(vistaCinemaIdForSearch)
          );
          if (matchingCinema && matchingCinema.id) {
            const fetchedCinema = await api.getCinemaById(matchingCinema.id);
            if (fetchedCinema) {
              dbCinemaName = fetchedCinema.name || dbCinemaName;
              dbCinemaAddress = fetchedCinema.address || fetchedCinema.location || dbCinemaAddress;
              dbCinemaId = fetchedCinema.id || dbCinemaId;
            }
          }
        } catch (nestedError) {
          console.warn('Failed to fetch cinema from database by Vista ID, using fallback data:', nestedError);
        }
      }

      const bookingData = {
        userId: userId,
        customerName: userDetails?.name || userDetails?.customerName || null,
        customerEmail: userDetails?.email || userDetails?.customerEmail || null,
        customerPhone: userDetails?.phone || userDetails?.mobile || userDetails?.customerPhone || null,
        movieId: movie?.id || null,
        vistaMovieId: vistaMovieId,
        movieTitle: movie?.title || 'Unknown Movie',
        movieRating: movie?.rating || null,
        cinemaId: dbCinemaId || null,
        vistaCinemaId: vistaCinemaId,
        cinemaName: dbCinemaName,
        cinemaLocation: dbCinemaAddress,
        screenName: showtime?.screenName || null,
        screenId: showtime?.screenId || showtime?.id || null,
        showId: vistaSessionId || showtime?.id || showtime?.vistaShowId || showtime?.showId || null,
        vistaShowId: vistaSessionId || showtime?.id || showtime?.vistaShowId || showtime?.showId || null,
        showDate: showDate,
        showTime: showTime,
        seats: selectedSeats.map(seat => seat.id || seat.name || seat).join(','),
        seatsArray: selectedSeats,
       totalAmount: getGrandTotal(), // ✅ send to backend
        ticketPrice: getTicketPrice(),
        ticketGST: getGST(),
        // snacksPrice: getSnacksPrice(),
        // snacksAmount: getSnacksPrice(),
        snacksPrice: getSnacksTotalWithGST(),
        snacksAmount: getSnacksTotalWithGST(),
        snacksGST: getSnacksGST(), 
        snacks: snacks || [],
        snacksDetails: snacks || [],
        convenienceFeeBase: getConvenienceFeeBase(),
        convenienceFeeGST: getConvenienceFeeGST(),
        taxesAndFees: getTaxesAndFees(),
        subtotal: getSubTotal(),
        couponCode: appliedCoupon?.code || null,
        couponId: appliedCoupon?.id || null,
        couponDiscount: couponDiscount || 0,
        moviePoster: movie?.poster || null,
        movieBanner: movie?.banner || null,
        movieImage: movie?.image || null,
        vistaTransactionId: vistaTransId,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
        },
      };

      const result = await api.createPendingBooking(bookingData);
      const bookingId = result.bookingId;
      setPendingBookingId(bookingId);
      // Store in sessionStorage for reliability (survives page refresh)
      sessionStorage.setItem('pendingBookingId', bookingId);
      return bookingId;
    } catch (error) {
      console.error('Error creating pending booking:', error);
      showError(error.message || 'Failed to create booking. Please try again.');
      throw error;
    } finally {
      setIsCreatingPendingBooking(false);
    }
  };

  const handlePaymentSuccess = async (response) => {
    // console.log('Payment successful:', response);
    // console.log('Payment response notes:', response.notes);
    
    // Show loading state
    setIsProcessingBooking(true);
    
    try {
      // Parse showtime date and time
      // Priority: showDateTime/startDateTime > originalDate > date > today
      let showDate = null;
      let showTime = null;
      
      // First, try to get from showDateTime or startDateTime (most reliable)
      if (showtime?.showDateTime || showtime?.startDateTime) {
        try {
          const dateTimeStr = showtime.showDateTime || showtime.startDateTime;
          const dateTime = new Date(dateTimeStr);
          if (!isNaN(dateTime.getTime())) {
            // Use local date methods to avoid timezone conversion issues
            // The API returns IST time, so we want to preserve the local date/time
            const year = dateTime.getFullYear();
            const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
            const day = dateTime.getDate().toString().padStart(2, '0');
            showDate = `${year}-${month}-${day}`;
            const hours = dateTime.getHours().toString().padStart(2, '0');
            const minutes = dateTime.getMinutes().toString().padStart(2, '0');
            showTime = `${hours}:${minutes}`;
          }
        } catch (e) {
          console.warn('Failed to parse showDateTime:', e);
        }
      }
      
      // If we have originalDate (from booking flow), use it
      if (!showDate && showtime?.originalDate) {
        showDate = showtime.originalDate;
      }
      
      // If we have originalTime (from booking flow), use it
      if (!showTime && showtime?.originalTime) {
        showTime = showtime.originalTime;
      }
      
      // Fallback to parsing date field
      if (!showDate && showtime?.date) {
        // If date is in format "Wed, 22 Oct", parse it
        if (showtime.date.includes(',')) {
          // Try to parse date like "Wed, 22 Oct"
          const dateParts = showtime.date.split(',');
          if (dateParts.length > 1) {
            const dayMonth = dateParts[1].trim().split(' ');
            const currentYear = new Date().getFullYear();
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthIndex = monthNames.findIndex(m => m === dayMonth[1]);
            if (monthIndex !== -1) {
              const date = new Date(currentYear, monthIndex, parseInt(dayMonth[0]));
              showDate = date.toISOString().split('T')[0];
            }
          }
        } else {
          // Assume it's already in YYYY-MM-DD format
          showDate = showtime.date;
        }
      }
      
      // Fallback to parsing time field
      if (!showTime && showtime?.time) {
        // Parse time like "7:50 PM" to "19:50"
        const timeStr = showtime.time.trim();
        const pmMatch = timeStr.match(/(\d+):(\d+)\s*(PM|pm)/);
        const amMatch = timeStr.match(/(\d+):(\d+)\s*(AM|am)/);
        if (pmMatch) {
          let hours = parseInt(pmMatch[1]);
          if (hours !== 12) hours += 12;
          const minutes = pmMatch[2].padStart(2, '0');
          showTime = `${hours}:${minutes}`;
        } else if (amMatch) {
          let hours = parseInt(amMatch[1]);
          if (hours === 12) hours = 0;
          const minutes = amMatch[2].padStart(2, '0');
          showTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
        } else {
          // Assume 24-hour format
          showTime = timeStr;
        }
      }
      
      // Last resort: use today's date (should not happen if showtime is properly set)
      if (!showDate) {
        console.warn('No show date found in showtime, using today as fallback:', showtime);
        showDate = new Date().toISOString().split('T')[0];
      }
      
      if (!showTime) {
        console.warn('No show time found in showtime, using 00:00 as fallback:', showtime);
        showTime = '00:00';
      }

      // Ensure we have valid seats
      if (!selectedSeats || selectedSeats.length === 0) {
        console.error('No seats selected');
        showError('No seats selected. Please go back and select seats.');
        return;
      }

      // For admin-created bookings, we might not have userId yet
      // Backend will handle user lookup/creation based on customer details
      // If we have paymentToken, backend will find the existing pending booking and update it
      const userId = userDetails?.userId || userDetails?.id || null;

      // Prepare booking data with complete details for invoice generation
      const ticketPriceBase = getTicketPrice();
      const ticketGSTAmount = getGST();
      // const snacksPriceAmount = getSnacksPrice();
      const snacksPriceAmount = getSnacksTotalWithGST();
      const convenienceFeeBaseAmount = getConvenienceFeeBase(); // Base amount (without GST)
      const convenienceFeeGSTAmount = getConvenienceFeeGST(); // GST portion extracted from total
      const taxesAndFeesAmount = getTaxesAndFees(); // Total of all taxes (GST + IGST)
      const subtotalAmount = getSubTotal(); // Base amounts without taxes (Ticket + Snacks + Convenience Fee)

      // Get exact Vista IDs - no fallbacks, must be exact Vista values
      const vistaMovieId = movie?.vistaMovieId || movie?.vistamovieid || null;
      const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid || null;

      // Log Vista IDs for debugging
      // console.log('Vista IDs extracted for booking:', {
      //   vistaMovieId,
      //   vistaCinemaId,
      //   movieId: movie?.id,
      //   cinemaId: theater?.id,
      //   movie: { ...movie, vistaMovieId: movie?.vistaMovieId || movie?.vistamovieid },
      //   theater: { ...theater, vistaCinemaId: theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid },
      // });

      // Fetch cinema data from database to ensure correct name and address
      let dbCinemaName = theater?.name || 'Unknown Cinema';
      let dbCinemaAddress = (theater?.location && theater.location.trim() !== '') ? theater.location : (theater?.address && theater.address.trim() !== '') ? theater.address : null;
      let dbCinemaId = theater?.id;

      // Check if dbCinemaId is a UUID format (contains hyphens) or a Vista cinema ID
      // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
      const isUUID = dbCinemaId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dbCinemaId);

      if (dbCinemaId && isUUID) {
        try {
          const cinemaData = await api.getCinemaById(dbCinemaId);
          if (cinemaData) {
            dbCinemaName = cinemaData.name || dbCinemaName;
            dbCinemaAddress = cinemaData.address || cinemaData.location || dbCinemaAddress;
            dbCinemaId = cinemaData.id || dbCinemaId;
          }
        } catch (error) {
          console.warn('Failed to fetch cinema from database by ID:', error);
        }
      }
      
      // If not a UUID or fetch failed, try to find by vistaCinemaId and then fetch by its actual ID
      const vistaCinemaIdForSearch = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid || (!isUUID ? dbCinemaId : null);
      if (vistaCinemaIdForSearch) {
        try {
          const allCinemas = await api.getCinemas({ limit: 1000 });
          const matchingCinema = allCinemas.find(c => 
            c.vistaCinemaId === vistaCinemaIdForSearch || 
            c.vistacinemaid === vistaCinemaIdForSearch || 
            c.vista_cinema_id === vistaCinemaIdForSearch ||
            String(c.vistaCinemaId) === String(vistaCinemaIdForSearch) ||
            String(c.vistacinemaid) === String(vistaCinemaIdForSearch) ||
            String(c.vista_cinema_id) === String(vistaCinemaIdForSearch)
          );
          if (matchingCinema && matchingCinema.id) {
            const fetchedCinema = await api.getCinemaById(matchingCinema.id);
            if (fetchedCinema) {
              dbCinemaName = fetchedCinema.name || dbCinemaName;
              dbCinemaAddress = fetchedCinema.address || fetchedCinema.location || dbCinemaAddress;
              dbCinemaId = fetchedCinema.id || dbCinemaId;
            }
          }
        } catch (nestedError) {
          console.warn('Failed to fetch cinema from database by Vista ID, using fallback data:', nestedError);
        }
      }

      // Get bookingId from multiple sources (in order of reliability):
      // 1. sessionStorage (most reliable - survives page refresh)
      // 2. React state
      // 3. Razorpay notes (if available)
      // commented out on march 12 in checking admin booking test
      // const storedBookingId = sessionStorage.getItem('pendingBookingId');
      // const bookingIdFromNotes = response.notes?.booking_id || 
      //                            response.notes?.bookingId || 
      //                            null;
      
      // // Use stored bookingId first, then state, then notes
      // const bookingIdToConfirm = storedBookingId || pendingBookingId || bookingIdFromNotes;
      
      // console.log('Confirming pending booking:', {
      //   storedBookingId,
      //   bookingIdFromNotes,
      //   pendingBookingId,
      //   bookingIdToConfirm,
      //   razorpayNotes: response.notes
      // });

      const storedBookingId = sessionStorage.getItem("pendingBookingId");

      const bookingIdFromNotes =
        response.notes?.booking_id || response.notes?.bookingId || null;

      // 🔹 NEW: get bookingId from Razorpay handler
      const bookingIdFromResponse = response.bookingId || null;

      const bookingIdToConfirm =
        storedBookingId ||
        pendingBookingId ||
        bookingIdFromResponse ||
        bookingIdFromNotes;

// console.log("Confirming pending booking:", {
//   storedBookingId,
//   pendingBookingId,
//   bookingIdFromResponse,
//   bookingIdFromNotes,
//   bookingIdToConfirm
// });
      
      // Clear stored bookingId after successful confirmation
      if (bookingIdToConfirm) {
        sessionStorage.removeItem('pendingBookingId');
      }

      // ALWAYS try to confirm pending booking first (never create new one)
      let bookingResult;
      if (bookingIdToConfirm) {
        // Confirm existing pending booking
        try {
          // Detect payment method from response
          const isEasebuzz = !!(response.easebuzzTransactionId || response.txnid || response.easebuzz_txnid || response.paymentMethod === 'Easebuzz');
          
          // Prepare payment data based on payment method
          const paymentConfirmData = isEasebuzz ? {
            transactionId: response.easebuzzTransactionId || response.txnid || response.transactionId || null,
            easebuzzTransactionId: response.easebuzzTransactionId || response.txnid || null,
            easebuzz_txnid: response.easebuzzTransactionId || response.txnid || null,
            paymentMethod: 'Easebuzz',
            paymentGatewayResponse: response,
            paymentResponse: response,
            totalAmount: getTicketPrice(),
          } : {
            transactionId: response.razorpay_payment_id || response.payment_id || response.transaction_id || null,
            razorpayPaymentId: response.razorpay_payment_id || null,
            razorpayOrderId: response.razorpay_order_id || null,
            paymentMethod: 'Razorpay',
            paymentGatewayResponse: response,
            paymentResponse: response,
            totalAmount: getTicketPrice(),
          };
          
          bookingResult = await api.confirmBooking(bookingIdToConfirm, paymentConfirmData);
        } catch (confirmError) {
          // If confirm fails (booking not found or already confirmed), log error
          console.error('Failed to confirm pending booking:', confirmError);
          // Check if it's a 404 (booking not found) or 400 (already confirmed)
          const status = confirmError.status ?? confirmError.response?.status;
          if (status === 404) {
            // Booking not found - this shouldn't happen, but log it
            console.error('Pending booking not found:', bookingIdToConfirm);
            showError('Booking not found. Please contact support with your payment ID: ' + (response.razorpay_payment_id || 'N/A'));
            return;
          } else if (status === 400) {
            // Already confirmed - fetch the booking details
            console.warn('Booking already confirmed, fetching details');
            try {
              // getBookingById already returns data.data (the booking object), not {success, data}
              bookingResult = await api.getBookingById(bookingIdToConfirm);
              if (!bookingResult) {
                throw new Error('Failed to fetch booking details');
              }
            } catch (fetchError) {
              console.error('Failed to fetch booking details:', fetchError);
              showError('Payment successful but unable to retrieve booking details. Please contact support.');
              return;
            }
          } else {
            // Other error - rethrow
            throw confirmError;
          }
        }
      } else {
        // No bookingId found - this is a serious error
        console.error('CRITICAL: No bookingId found in payment response or state!', {
          response,
          pendingBookingId,
          bookingIdFromNotes
        });
        showError('Payment successful but booking ID not found. Please contact support with your payment ID: ' + (response.razorpay_payment_id || 'N/A'));
        return;
      }
      
      // console.log('Booking confirmed successfully:', bookingResult);
      
      // DEBUG: Show Vista commit API response in popup
      const commitResponse = bookingResult?.vistaCommitResponse;
      if (commitResponse) {
        // Show debug popup with commit response
        const commitResponseJson = JSON.stringify(commitResponse, null, 2);
        showError(`DEBUG: Vista Commit API Response\n\n${commitResponseJson}`);
      }
      
      // Store all data needed for confirmation BEFORE clearing the booking context
      // Calculate and store total amount before clearing
      const totalAmount = getGrandTotal();
      
      // Preserve structured seat data so the confirmation popup can group by category
      // Each seat object already carries displayNumber, ticketTypeDescription, areaName, category
      const structuredSeats = Array.isArray(selectedSeats)
        ? selectedSeats.map((seat) => ({
            displayNumber:
              seat.displayNumber ||
              `${seat.row || ''}${seat.number || ''}` ||
              seat.id ||
              seat.name ||
              '',
            ticketTypeDescription: seat.ticketTypeDescription || null,
            areaName: seat.areaName || null,
            category: seat.category || null,
          }))
        : [];

      const ticketCount = Array.isArray(selectedSeats) ? selectedSeats.length : 0;

      // Use the cinema data that was already fetched earlier in handlePaymentSuccess
      // dbCinemaName and dbCinemaAddress are already set from the fetch logic above (around line 1126)

      // Prepare confirmation details for popup (store all data before clearing)
      const derivedStatus =
  bookingResult?.bookingStatus ||
  bookingResult?.uiStatus ||
  bookingResult?.status ||
  'pending';
    
      const confirmationDetails = {
        bookingId: bookingResult?.bookingId,
        myvistaBookingId: bookingResult?.myvistaBookingId,
        myvistaBookingCode: bookingResult?.myvistaBookingCode, // Vista booking code (prioritized for display)
        qrCodeUrl: bookingResult?.qrCodeUrl, // QR code URL
        status: derivedStatus,
        movieTitle: movie?.title || 'Unknown Movie',
        cinemaName: dbCinemaName,
        cinemaAddress: dbCinemaAddress,
        showDate: showDate,
        showTime: showtime?.time || showTime,
        screenName: showtime?.screenName || showtime?.screen?.name || globalShowtime?.screenName || null,
        seats: structuredSeats,
        ticketCount: ticketCount,
        totalAmount: totalAmount,
        
        pricing: {
          ticketPrice: getTicketPrice(),
          // snacksAmount: getSnacksPrice(),
          snacksAmount: getSnacksTotalWithGST(),
          convenienceFeeBase: getConvenienceFeeBase(),
          convenienceFeeGST: getConvenienceFeeGST(),
          couponDiscount: couponDiscount,
          totalAmount: totalAmount,
        },
      
        // Optional but nice for UI
        couponCode: appliedCoupon?.code || null,
      
      };

      // Hide loading state
      setIsProcessingBooking(false);

      // Show confirmation popup FIRST (before clearing)
      setBookingConfirmationDetails(confirmationDetails);
      setShowConfirmationPopup(true);
      
      // Clear booking context (cart) AFTER showing popup
      // Also clear pendingBookingId from state and sessionStorage
      setPendingBookingId(null);
      sessionStorage.removeItem('pendingBookingId');
      
      // Use setTimeout to ensure popup state is set before clearing
      setTimeout(() => {
        clearBooking();
      }, 100);
      
    } catch (error) {
      // Hide loading state on error
      setIsProcessingBooking(false);
      
      console.error('Error saving booking:', error);
      console.error('Error details:', error.response || error.message);
      const errorMessage = error.response?.message || error.message || 'Unknown error';
      showError(`Payment was successful, but there was an error saving your booking: ${errorMessage}. Please contact support with payment ID: ${response.razorpay_payment_id || 'N/A'}`);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationPopup(false);
    setBookingConfirmationDetails(null);
    // Navigate to home page after closing confirmation
    navigate('/');
  };

  const handlePaymentError = (error) => {
    console.log('Payment failed:', error);
    showError('Payment failed. Please try again.');
  };

  const getTicketPrice = () => {
    // For payment link bookings, use the ticket price from booking
    if (paymentToken && bookingFromToken) {
      // Check multiple possible paths for ticket price
      const ticketPrice = 
        bookingFromToken.showtime?.price?.ticketPrice ||
        bookingFromToken.bookingDetails?.pricing?.ticketPrice ||
        bookingFromToken.ticketPrice ||
        0;
      return parseFloat(ticketPrice || 0);
    }
    return selectedSeats.reduce((total, seat) => total + (seat.price || 200), 0);
  };

  const getGST = () => {
    if (paymentToken && bookingFromToken) {
      const ticketGST =
        bookingFromToken.showtime?.price?.ticketGST ||
        bookingFromToken.bookingDetails?.pricing?.ticketGST ||
        0;
      return parseFloat(ticketGST || 0);
    }
  
    const total = getTicketPrice(); // already GST included
  
    const base = total / 1.18;
    const gst = base * 0.18;
  
    return Math.round(gst); // or roundTo2(gst) if you want decimals
  };

  const getSnacksGST = () => {
    const total = getSnacksTotalWithGST(); // already GST included
    const gst = total * (fbGstPercent / (100 + fbGstPercent));
    return roundTo2(gst);
  };

  const getSnacksPrice = () => {
    // For payment link bookings, get snacks price from booking data
    if (paymentToken && bookingFromToken) {
      // Check multiple possible paths for snacks price
      const snacksPrice = 
        bookingFromToken.showtime?.price?.snacksAmount ||
        bookingFromToken.bookingDetails?.pricing?.snacksAmount ||
        0;
      return parseFloat(snacksPrice || 0);
    }
    return snacks.reduce((total, snack) => total + (snack.price * snack.quantity), 0);
  };

  // Calculate convenience fee (per seat)
  // No convenience fee for payment link bookings (already included in total)
  const getConvenienceFee = () => {
    // No convenience fee for payment link bookings
    if (paymentToken) {
      return 0;
    }
    
    if (!selectedSeats || selectedSeats.length === 0) {
      return 0;
    }

    // Use tiered booking fee system if tiers are available
    if (bookingFeeTiers && bookingFeeTiers.length > 0) {
      const totalTicketPrice = getTicketPrice();
      const seatCount = selectedSeats.length;
      
      // Find the appropriate tier for the total ticket price
      const matchingTier = bookingFeeTiers.find(tier => 
        totalTicketPrice >= tier.min_price && totalTicketPrice <= tier.max_price
      );
      
      if (matchingTier) {
        // Booking fee is per seat, so multiply by seat count
        const feePerSeat = typeof matchingTier.booking_fee === 'number' 
          ? matchingTier.booking_fee 
          : parseFloat(matchingTier.booking_fee) || 0;
        return feePerSeat * seatCount;
      }
      
      // No matching tier found, return 0
      return 0;
    }

    // Fall back to old fixed/percentage system if tiers are not available
    if (!convenienceFeeSettings.enabled) {
      return 0;
    }

    const seatCount = selectedSeats.length;
    const { type, value } = convenienceFeeSettings;

    if (type === 'fixed') {
      // Fixed amount per seat
      return value * seatCount;
    } else if (type === 'percentage') {
      // Percentage of total seat price
      const totalSeatPrice = getTicketPrice();
      return (totalSeatPrice * value) / 100;
    }

    return 0;
  };

  // Get base convenience fee (without GST)
  // The booking fee already includes GST, so we need to extract the base amount
  const getConvenienceFeeBase = () => {
    const totalConvenienceFee = getConvenienceFee();
    if (totalConvenienceFee === 0) return 0;
    // If total includes 18% GST: Base = Total / 1.18
    const baseAmount = totalConvenienceFee / 1.18;
    return baseAmount;
  };

  // Calculate GST on convenience fee (extract GST from total)
  // The booking fee already includes GST, so we extract the GST portion
  const getConvenienceFeeGST = () => {
    const totalConvenienceFee = getConvenienceFee();
    if (totalConvenienceFee === 0) return 0;
    // If total includes 18% GST: GST = Total - Base = Total * (0.18 / 1.18)
    const baseAmount = totalConvenienceFee / 1.18;
    const gstAmount = totalConvenienceFee - baseAmount;
    return gstAmount;
  };

  const getSubTotal = () => {
    // Subtotal = Base amounts WITHOUT taxes
    // Includes: Ticket Price + Snacks + Convenience Fee Base (no GST/IGST)
    // return getTicketPrice() + getSnacksPrice() + getConvenienceFeeBase();
    return getTicketPrice() + getSnacksTotalWithGST() + getConvenienceFeeBase();
  };

  const getTaxesAndFees = () => {
    // For payment link bookings, get taxes from booking data
    if (paymentToken && bookingFromToken) {
      // Check multiple possible paths for taxes and fees
      const taxesAndFees = 
        bookingFromToken.showtime?.price?.taxesAndFees ||
        bookingFromToken.bookingDetails?.pricing?.taxesAndFees ||
        0;
      return parseFloat(taxesAndFees || 0);
    }
    // Taxes & Fees = TOTAL of all taxes collected
    // Includes: GST on tickets + IGST on convenience fee + snackgst
    // This is the total tax amount, not an additional tax
    const gstOnTickets = getGST();
    const igstOnConvenienceFee = getConvenienceFeeGST();
    const snacksGST = getSnacksGST();
    return gstOnTickets + snacksGST + igstOnConvenienceFee;
  };

  const getGrandTotal = () => {
    // Payment link flow (keep as-is)
    if (paymentToken && bookingFromToken) {
      const total = parseFloat(bookingFromToken.totalAmount || 0);
      const finalTotal = total - couponDiscount;
      return Math.max(0, roundTo2(finalTotal));
    }
  
    // Ticket price (no GST)
    const ticketPriceTotal = getTicketPrice();
  
    // ✅ Snacks WITH GST (FIX)
    const snacksBase = getSnacksPrice();
    const snacksWithGST = roundTo2(
      snacksBase * (1 + (settings?.fnbGst || 0) / 100)
    );

  
  
    // Convenience fee (already includes GST)
    const convenienceFeeTotal = getConvenienceFee();
  
    // Total
    const total = ticketPriceTotal + snacksWithGST + convenienceFeeTotal;
  
    // Apply coupon
    const finalTotal = total - couponDiscount;
  
    return Math.max(0, roundTo2(finalTotal));
  };
  

  // Handle coupon code validation and application
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    // Prevent applying if already applied
    if (appliedCoupon) {
      setCouponError('A coupon is already applied. Remove it first to apply a new one.');
      return;
    }

    // Prevent double-clicking
    if (validatingCoupon) {
      return;
    }

    try {
      setValidatingCoupon(true);
      setCouponError('');

      const ticketPrice = getTicketPrice();
      // const snacksPrice = getSnacksPrice();
      const snacksPrice = getSnacksTotalWithGST();
      const convenienceFeeBase = getConvenienceFeeBase();
      const userId = userDetails?.userId || userDetails?.id || null;
      
      const response = await api.validateCoupon(
        couponCode.trim(),
        ticketPrice,
        snacksPrice,
        convenienceFeeBase,
        userId
      );

      if (response.success && response.data) {
        setAppliedCoupon(response.data.coupon);
        setCouponDiscount(response.data.discountAmount);
        setCouponError('');
      } else {
        setCouponError(response.error || 'Invalid coupon code');
        setAppliedCoupon(null);
        setCouponDiscount(0);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError(error.message || 'Failed to validate coupon. Please try again.');
      setAppliedCoupon(null);
      setCouponDiscount(0);
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCode('');
    setCouponError('');
  };

  return (
    <>
      {/* Cancel Transaction Confirmation Popup */}
      <CancelTransactionPopup
        isOpen={showCancelConfirm}
        onConfirm={handleCancelTransaction}
        onCancel={() => setShowCancelConfirm(false)}
        isProcessing={isCancellingTransaction}
      />

      {/* Quick Navigation */}
      {/* <div className="quicknav">
        <div className="container">
          <ul>
            <li><button className="nav-link">Group Booking</button></li>
            <li><button className="nav-link">Offers & Promotion</button></li>
            <li><button className="nav-link">FAQ</button></li>
            <li><Link to="/contact-us" className="nav-link">Contact Us</Link></li>
          </ul>
        </div>
      </div> */}

      {/* Booking Progress */}
      <div className="booking-progress booking-progress--snacks">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
          <button
  className="secondary-btn"
  onClick={() => setShowCancelConfirm(true)}
  disabled={isProcessingBooking}
>
  <i className="fas fa-arrow-left" style={{ fontSize: '1rem' }}></i>
</button>
            {!paymentToken && movie && showtime && theater && (
              <ProgressStepper
                currentStep="payment"
                movie={movie}
                showtime={showtime}
                theater={theater}
                selectedSeats={selectedSeats}
              />
            )}
            {paymentToken && (
              <div className="payment-link-indicator">
                <span className="badge bg-primary">Payment Link</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Content */}
      <div className="payment-container">
        <div className="container">
          {loadingBookingFromToken && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading booking...</span>
              </div>
              <p className="mt-3">Loading your booking details...</p>
            </div>
          )}
          {!loadingBookingFromToken && (
            <div className="row">
              <div className="col-lg-8">
                {isPhoneVerified || paymentToken ? (
                  <>
                    {/* Payment Summary Header for payment link access */}
                    {paymentToken && (
                      <div className="payment-link-header mb-4">
                        <h2>Complete Your Payment</h2>
                        <p className="text-muted">
                          Your booking has been created. Please complete the
                          payment to confirm your tickets.
                        </p>
                      </div>
                    )}
                   {!loadingBookingFromToken && (userDetails || paymentToken) && (
  <div className="payment-options-container">
                        {/* Payment Summary - Show once at top */}
                        <div className="payment-summary-section mb-4">
                          <h3>Payment Summary</h3>
                          <div className="amount-breakdown">
                            <div className="amount-item">
                              <span>
                                Net Price ({selectedSeats?.length} x Ticket(s))
                              </span>
                              <span>₹{getTicketPrice().toFixed(2)}</span>
                            </div>

                            <div className="amount-item">
                              <span>Snacks</span>
                              <span>
                              {/* ₹{paymentToken ? "0.00" : getSnacksTotalWithGST().toFixed(2)} */}
                              ₹{paymentToken 
                              ? (0).toFixed(2) 
                              : (getSnacksTotalWithGST() || 0).toFixed(2)}
                              </span>
                            </div>

                            {getConvenienceFee() > 0 && (
                              <div className="amount-item">
                                <span>Convenience Fee</span>
                                <span>₹{getConvenienceFee().toFixed(2)}</span>
                              </div>
                            )}

                            {couponDiscount > 0 && (
                              <div
                                className="amount-item"
                                style={{ color: "#28a745", fontWeight: "600" }}
                              >
                                <span>
                                  Coupon Discount ({appliedCoupon?.code})
                                </span>
                                <span>-₹{couponDiscount.toFixed(2)}</span>
                              </div>
                            )}

                            <div className="amount-item total">
                              <span>Total Amount</span>
                              <span>₹{getGrandTotal().toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Choose Payment Method - Show both options */}
                        <div className="payment-methods-section">
                          <h4>Choose Payment Method</h4>

                          {/* Shared Terms and Conditions Checkbox */}
                          <div className="shared-terms-checkbox-container">
                            <label className="shared-terms-checkbox-label">
                              <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) =>
                                  setTermsAccepted(e.target.checked)
                                }
                                className="shared-terms-checkbox"
                              />
                              <span className="shared-terms-text">
                                By proceeding with payment, you acknowledge that
                                you have read, understood, and agreed to MY
                                Cinemas'{" "}
                                <button
                                  onClick={() =>
                                    handleTermsLinkClick("terms-and-conditions")
                                  }
                                  className="shared-terms-link"
                                >
                                  Terms & Conditions
                                </button>
                                ,{" "}
                                <button
                                  onClick={() =>
                                    handleTermsLinkClick(
                                      "refund-cancellation-policy",
                                    )
                                  }
                                  className="shared-terms-link"
                                >
                                  Refund & Cancellation Policy
                                </button>
                                , and{" "}
                                <button
                                  onClick={() =>
                                    handleTermsLinkClick("privacy-policy")
                                  }
                                  className="shared-terms-link"
                                >
                                  Privacy Policy
                                </button>
                                .
                              </span>
                            </label>
                          </div>

                          <div className="payment-gateway-options">
                            {/* Show payment gateway based on admin settings */}
                            {paymentSettings?.paymentGateway === "razorpay" && (
                              <RazorpayPayment
                                key={`razorpay-${paymentToken || "normal"}-${selectedSeats?.length || 0}`}
                                amount={getGrandTotal()}
                                currency="INR"
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                userDetails={userDetails}
                                // bookingId={pendingBookingId}
                                bookingId={pendingBookingId || bookingFromToken?.bookingId || bookingFromToken?.id}
                                onCreatePendingBooking={
                                  createPendingBookingBeforePayment
                                }
                                isCreatingPendingBooking={
                                  isCreatingPendingBooking
                                }
                                bookingDetails={{
                                  movie,
                                  showtime,
                                  theater,
                                  seats: selectedSeats,
                                  snacks: paymentToken ? [] : snacks,
                                  ticketPrice: getTicketPrice() + getGST(),
                                  // snacksPrice: paymentToken
                                  //   ? 0
                                  //   : getSnacksPrice(),
                                  snacksPrice: paymentToken
                                    ? 0
                                    : getSnacksTotalWithGST(),
                                  taxes: getTaxesAndFees(),
                                  bookingId:
                                    pendingBookingId || `BOOK_${Date.now()}`,
                                }}
                                showSummary={false}
                                showTerms={false}
                                termsAccepted={termsAccepted}
                              />
                            )}

                            {paymentSettings?.paymentGateway === "easebuzz" && (
                              <EasebuzzPayment
                                key={`easebuzz-${paymentToken || "normal"}-${selectedSeats?.length || 0}`}
                                amount={getGrandTotal()}
                                currency="INR"
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                userDetails={userDetails}
                                bookingId={pendingBookingId}
                                onCreatePendingBooking={
                                  createPendingBookingBeforePayment
                                }
                                isCreatingPendingBooking={
                                  isCreatingPendingBooking
                                }
                                bookingDetails={{
                                  movie,
                                  showtime,
                                  theater,
                                  seats: selectedSeats,
                                  snacks: paymentToken ? [] : snacks,
                                  ticketPrice: getTicketPrice() + getGST(),
                                  // snacksPrice: paymentToken
                                  //   ? 0
                                  //   : getSnacksPrice(),
                                  snacksPrice: paymentToken
                                    ? 0
                                    : getSnacksTotalWithGST(),
                                  taxes: getTaxesAndFees(),
                                  bookingId:
                                    pendingBookingId || `BOOK_${Date.now()}`,
                                }}
                                showSummary={false}
                                showTerms={false}
                                termsAccepted={termsAccepted}
                              />
                            )}

                            {/* Fallback to Razorpay if no payment gateway is set */}
                            {!paymentSettings?.paymentGateway && (
                              <RazorpayPayment
                                key={`razorpay-${paymentToken || "normal"}-${selectedSeats?.length || 0}`}
                                amount={getGrandTotal()}
                                currency="INR"
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                userDetails={userDetails}
                                bookingId={pendingBookingId}
                                onCreatePendingBooking={
                                  createPendingBookingBeforePayment
                                }
                                isCreatingPendingBooking={
                                  isCreatingPendingBooking
                                }
                                bookingDetails={{
                                  movie,
                                  showtime,
                                  theater,
                                  seats: selectedSeats,
                                  snacks: paymentToken ? [] : snacks,
                                  ticketPrice: getTicketPrice() + getGST(),
                                  // snacksPrice: paymentToken
                                  //   ? 0
                                  //   : getSnacksPrice(),
                                  snacksPrice: paymentToken
                                    ? 0
                                    : getSnacksTotalWithGST(),
                                  taxes: getTaxesAndFees(),
                                  bookingId:
                                    pendingBookingId || `BOOK_${Date.now()}`,
                                }}
                                showSummary={false}
                                showTerms={false}
                                termsAccepted={termsAccepted}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="payment-placeholder">
                    <div className="placeholder-content">
                      <h3>Complete Verification</h3>
                      <p>
                        Please verify your phone number to proceed with payment.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-lg-4">
                <div className="booking-summary-sidebar">
                  <h4>Booking Summary</h4>

                  <div className="movie-info">
                    <img
                      src={getMovieImage(movie)}
                      alt={movie?.title || "Movie"}
                    />
                    <div className="movie-details">
                      <h5>  {movie.title.replace(/\(.*?\)/g, '').trim()}
                      </h5>
                      <div className="movie-tags">
                        {movie?.genre &&
                          String(movie.genre)
                            .replace(/[{}"]/g, "")
                            .split(",")
                            .map((g) => g.trim())
                            .filter(Boolean)
                            .map((g, i) => (
                              <span key={i} className="sc-tag">
                                {g}
                              </span>
                            ))}
                        {movie?.language && (
                          <span className="tag language">{movie.language}</span>
                        )}
                        {(movie?.ageRating || movie?.rating) && (
                          <span className="tag age-rating">
                            {movie?.ageRating || movie?.rating}
                          </span>
                        )}
                        {movie?.warning && (
                          <span className="tag warning">{movie.warning}</span>
                        )}
                      </div>
                      <p>
                        {dbCinemaData?.name || theater?.name || "My Cinemas"}
                        {showtime?.screenName && ` (${showtime.screenName})`}
                      </p>
                      <p>
                        {dbCinemaData?.address ||
                          dbCinemaData?.location ||
                          theater?.address ||
                          theater?.location ||
                          ""}
                      </p>
                      <p>
                        {showtime?.date || "Wed, 22 Oct"} •{" "}
                        {showtime?.time || "7:50 PM"}
                      </p>
                    </div>
                  </div>

                  <div className="seat-info">
                    <h4>Seat Info</h4>
                    <div className="seat-items-container">
                      {selectedSeats && selectedSeats.length > 0 ? (
                        <div className="selected-seats-list">
                          {selectedSeats.map((seat, index) => (
                            <div key={seat.id || index} className="seat-item">
                              <span className="seat-category">
                                {seat.ticketTypeDescription ||
                                  seat.areaName ||
                                  seat.category?.toUpperCase() ||
                                  "REGULAR"}
                              </span>
                              <span className="seat-number">
                                {seat.displayNumber ||
                                  `${seat.row}${seat.number}` ||
                                  seat.id ||
                                  `Seat ${index + 1}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="seat-item">
                          <span className="seat-category">REGULAR</span>
                          <span className="seat-number">A1</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Coupon Code Section */}
                  <div
                    className="coupon-section"
                    style={{
                      marginBottom: "20px",
                      paddingBottom: "20px",
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <h5
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        marginBottom: "12px",
                        color: "#495057",
                      }}
                    >
                      Have a Coupon Code?
                    </h5>
                    {!appliedCoupon ? (
                      <div style={{ display: "flex", gap: "8px" }}>
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) =>
                            setCouponCode(e.target.value.toUpperCase())
                          }
                          placeholder="Enter coupon code"
                          style={{
                            flex: 1,
                            padding: "10px 12px",
                            border: couponError
                              ? "2px solid #dc3545"
                              : "1px solid #dee2e6",
                            borderRadius: "6px",
                            fontSize: "14px",
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleApplyCoupon();
                            }
                          }}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon || !couponCode.trim()}
                          style={{
                            padding: "10px 20px",
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor:
                              validatingCoupon || !couponCode.trim()
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              validatingCoupon || !couponCode.trim() ? 0.6 : 1,
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          {validatingCoupon ? "Applying..." : "Apply"}
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px",
                          backgroundColor: "#d4edda",
                          borderRadius: "6px",
                          border: "1px solid #c3e6cb",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              color: "#155724",
                              fontSize: "14px",
                            }}
                          >
                            {appliedCoupon.code} - {appliedCoupon.name}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#155724",
                              marginTop: "4px",
                            }}
                          >
                            Discount: ₹{couponDiscount.toFixed(2)}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveCoupon}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {couponError && (
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "8px 12px",
                          backgroundColor: "#f8d7da",
                          color: "#721c24",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        {couponError}
                      </div>
                    )}
                  </div>

                  <div className="payment-breakdown">
                    {paymentToken && bookingFromToken ? (
                      // Breakdown for payment link bookings - show all details but use booking amounts
                      <>
                        <div className="breakdown-item">
                          <span>
                            Net Price (
                            {bookingFromToken.seatCount || selectedSeats.length}{" "}
                            x Ticket(s))
                          </span>
                          <span>₹{getTicketPrice().toFixed(2)}</span>
                        </div>
                        {showLaunchOfferLine() && (
                          <div
                            className="breakdown-item"
                            style={{ color: "#28a745", fontWeight: "500" }}
                          >
                            <span>
                              🎁 {launchOfferLabel()} (Website Launch Offer)
                            </span>
                            <span>FREE</span>
                          </div>
                        )}
                        <div className="breakdown-item grand-total">
                          <span>Grand Total</span>
                          <span>₹{getGrandTotal().toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      // Full breakdown for normal bookings
                      <>
                        <div className="breakdown-item">
                          <span>
                            Net Price ({selectedSeats.length} x Ticket(s))
                          </span>
                          <span>₹{getTicketPrice().toFixed(2)}</span>
                        </div>

                        {snacks.length > 0 ? (
                          <div className="snacks-breakdown">
                            {snacks.map((snack) => (
                              <div key={snack.id} className="breakdown-item">
                                <span>
                                  {snack.name} x{snack.quantity}
                                </span>
                                <span>
                                ₹{roundTo2((snack.price * snack.quantity) * (1 + fbGstPercent / 100))}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-snacks">
                            <span>🍿🥤</span>
                            <span>No snacks added</span>
                          </div>
                        )}

                        {getConvenienceFee() > 0 && (
                          <div className="convenience-fee-section">
                            <div className="convenience-fee-header">
                              <span>Convenience Fees</span>
                              <span>₹{getConvenienceFee().toFixed(2)}</span>
                            </div>
                            <div className="convenience-fee-details">
                              <div className="convenience-fee-item">
                                <span>Base Amount</span>
                                <span>
                                  ₹{getConvenienceFeeBase().toFixed(2)}
                                </span>
                              </div>
                              <div className="convenience-fee-item">
                                <span>Integrated GST (IGST) @ 18%</span>
                                <span>
                                  ₹{getConvenienceFeeGST().toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {couponDiscount > 0 && (
                          <div
                            className="breakdown-item"
                            style={{ color: "#28a745", fontWeight: "600" }}
                          >
                            <span>Coupon Discount ({appliedCoupon?.code})</span>
                            <span>-₹{couponDiscount.toFixed(2)}</span>
                          </div>
                        )}

                        {showLaunchOfferLine() && (
                          <div
                            className="breakdown-item"
                            style={{ color: "#28a745", fontWeight: "500" }}
                            data-testid="launch-offer-line"
                          >
                            <span>
                              🎁 {launchOfferLabel()} (Website Launch Offer)
                            </span>
                            <span>FREE</span>
                          </div>
                        )}

                        <div className="breakdown-item grand-total">
                          <span>Grand Total</span>
                          <span>₹{getGrandTotal().toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phone Verification Popup - Only render when needed */}
      {showPhonePopup && (
        <PhoneVerificationPopup
          isOpen={showPhonePopup}
          onClose={handleClosePhonePopup}
          onPhoneVerified={handlePhoneVerified}
          onGoogleSignIn={handleGoogleSignIn}
          onShowRegistration={handleShowRegistration}
          onSwitchToGuest={handleSwitchToGuest}
        />
      )}

      {/* Guest Checkout Popup - Only render when needed */}
      {showGuestCheckoutPopup && (
        <GuestCheckoutPopup
          isOpen={showGuestCheckoutPopup}
          onClose={handleCloseGuestCheckoutPopup}
          onGuestDetailsSubmit={handleGuestDetailsSubmit}
          onSwitchToLogin={handleSwitchToLogin}
        />
      )}

      {/* OTP Entry Popup - Only render when needed */}
      {showOTPPopup && (
        <OTPEntryPopup
          isOpen={showOTPPopup}
          onClose={handleCloseOTPPopup}
          onOTPVerified={handleOTPVerified}
          phoneNumber={otpPhone}
          onResendOTP={handleResendOTP}
        />
      )}

      {/* Google-user mobile capture modal (BMS-style: no OTP) */}
      {showGoogleMobileModal && (
        <div
          className="phone-verification-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              // Don't allow dismiss without providing mobile
            }
          }}
        >
          <div className="phone-verification-popup">
            <div className="popup-content">
              <h2 className="popup-title">Almost there!</h2>
              <p className="popup-subtitle">
                Please share your mobile number so we can send your ticket details.
              </p>

              <div className="phone-input-container">
                <div className="phone-input-wrapper">
                  <div className="country-code">
                    <span className="country-flag">IN</span>
                    <span className="country-dial">+91</span>
                  </div>
                  <input
                    type="tel"
                    className="phone-input"
                    placeholder="10-digit mobile number"
                    value={googleMobile}
                    onChange={(e) =>
                      setGoogleMobile(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    autoFocus
                    maxLength={10}
                    autoComplete="off"
                  />
                </div>
              </div>

              {googleMobileError && (
                <div className="error-popup-message" style={{ marginTop: 10 }}>
                  {googleMobileError}
                </div>
              )}

              <p className="popup-subtitle" style={{ fontSize: 12 }}>
                We'll send your ticket via SMS and WhatsApp on this number.
              </p>

              <div className="form-actions">
                <button
                  type="button"
                  className="proceed-btn"
                  onClick={handleGoogleMobileSubmit}
                  disabled={
                    googleMobileSubmitting || googleMobile.length !== 10
                  }
                >
                  {googleMobileSubmitting ? "Saving..." : "Continue"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Popup - Only render when needed */}
      {showRegistrationPopup && (
        <RegistrationPopup
          isOpen={showRegistrationPopup}
          onClose={handleCloseRegistrationPopup}
          onRegistrationComplete={handleRegistrationComplete}
          phoneNumber={registrationPhone}
        />
      )}

      {/* Processing Loader - Show while booking is being processed */}
      {isProcessingBooking && (
        <div className="processing-overlay">
          <div className="processing-loader">
            <div className="spinner"></div>
            <p className="processing-text">Processing your booking...</p>
            <p className="processing-subtext">
              Please wait while we confirm your booking
            </p>
          </div>
        </div>
      )}

      {/* Booking Confirmation Popup - Only render when needed */}
      {showConfirmationPopup && bookingConfirmationDetails && (
        <BookingConfirmationPopup
          isOpen={showConfirmationPopup}
          onClose={handleConfirmationClose}
          bookingDetails={bookingConfirmationDetails}
        />
      )}

      {showErrorPopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowErrorPopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">{errorMessage}</div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Terms & Conditions Popup */}
      <TermsConditionsPopup
        isOpen={showTermsPopup}
        onClose={handleCloseTermsPopup}
        contentType={termsContentType}
        onTermsAccepted={handleTermsAccepted}
      />

      {/* FAB Button */}
      {/* <div className="fab-btn">
        <button className="primary-btn help-btn">
          <img src="/assets/img/icons/help.svg" alt="Help" /> Help
        </button>
        <button className="primary-btn fab-mob pulse">
          <i className="fas fa-ticket"></i>
        </button>
      </div> */}
    </>
  );
};

export default PaymentPage;